import { UFile, UFileStream } from '@w/utils'
import type { BucketDriver } from '../../src/types'

export class BucketBrowserDriver implements BucketDriver {
  __bucket__ = {
    logging: false,
  }

  constructor(private cacheStorage: CacheStorage = globalThis.caches) {}

  put = async (file: UFile | UFileStream) => {
    if (this.__bucket__.logging)
      console.info('BucketBrowserDriver.put:', file.path, file.size)

    if (!(file instanceof UFile || file instanceof UFileStream)) {
      throw new Error('Invalid file type. Expected UFile or UFileStream.')
    }

    const cache = await this.cacheStorage.open('bucket-storage')

    const headers = {
      ...file.headers(),
      'X-Cached-Time': new Date().toISOString(),
    }

    if (file instanceof UFile) {
      const response = new Response(file.blob, { headers })
      await cache.put(file.path, response)
      return { size: file.size }
    }

    if (file instanceof UFileStream) {
      const response = new Response(file.stream, { headers })
      await cache.put(file.path, response)
      return { size: file.size }
    }

    throw new Error('Invalid file type. Expected UFile or UFileStream.')
  }

  get = async (id: string) => {
    if (this.__bucket__.logging) console.info('BucketBrowserDriver.get:', id)

    const cache = await this.cacheStorage.open('bucket-storage')
    const response = await cache.match(id)

    if (!response) return null

    const blob = await response.blob()

    // Restore metadata from headers
    const metaHeader = response.headers.get('X-UFile-Meta')
    const meta = metaHeader
      ? (JSON.parse(decodeURIComponent(metaHeader)) as Record<string, unknown>)
      : undefined

    return new UFile(blob, { path: id, meta })
  }

  delete = async (ids: string | string[]) => {
    if (this.__bucket__.logging)
      console.info('BucketBrowserDriver.delete:', ids)

    const cache = await this.cacheStorage.open('bucket-storage')
    const idsToDelete = Array.isArray(ids) ? ids : [ids]

    await Promise.all(idsToDelete.map((id) => cache.delete(id)))
  }

  move = async (from: string, to: string) => {
    if (this.__bucket__.logging)
      console.info('BucketBrowserDriver.move:', from, to)

    const file = await this.get(from)
    if (!file) throw new Error(`File not found: ${from}`)
    await this.put(new UFile(file.blob, { path: to, meta: file.meta }))
    await this.delete(from)
    return file.size
  }

  list = async (prefix = ''): Promise<string[]> => {
    if (this.__bucket__.logging) console.info('BucketBrowserDriver.list')

    const cache = await this.cacheStorage.open('bucket-storage')
    const requests = await cache.keys()
    return requests.map((req) => new URL(req.url).pathname.slice(1)).filter(path => path.startsWith(prefix))
  }

  _clear = async () => {
    if (this.__bucket__.logging) console.info('BucketBrowserDriver._clear')

    await this.cacheStorage.delete('bucket-storage')
  }
}

export function createBucketBrowserDriver(options: {
  cacheStorage?: CacheStorage
} = {}): BucketBrowserDriver {
  return new BucketBrowserDriver(options.cacheStorage)
}
